import "antd/dist/antd.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import React, { FC, ComponentType, useEffect } from "react";
import {
    Navigate,
    useRoutes,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import loadable from "@loadable/component";

import { SUPPORTED } from "./i18n";
import {
    langFromUrl,
    resolveLang,
    setLangCookie,
} from "./utils/lang";
import { isPrerender } from "./utils/prerender";

const BackToTop = loadable(() => import("./components/BackToTop/BackToTop"));
const Pages = loadable(() => import("./components/Main"));
const Demo = loadable(() => import("./components/Demo/Demo"));
// Public, benefit-only WyKey page at /wykey and the Offers page at
// /wykey/offer — both always compiled and de-indexed (offer is noindex + in
// robots; it sits behind the reverse-proxy auth in production).
const WyKeyLight = loadable(() => import("./components/WyKey/Light/LightPage"));
const Offer = loadable(() => import("./components/WyKey/Offer/OfferPage"));

// The full technical page (/wykey/full) is only compiled into the bundle when
// REACT_APP_INCLUDE_PROTECTED === "true" (the `build:full` / internal build).
// CRA's DefinePlugin replaces the env var with a literal at build time, so in
// the default public build the import() below sits in a dead branch and webpack
// never emits its chunk — the full page's source is not downloadable from the
// public site at all. When excluded, /wykey/full redirects to the public page.
const INCLUDE_FULL = process.env.REACT_APP_INCLUDE_PROTECTED === "true";

let WyKey: ComponentType = () => <Navigate to="/wykey" replace />;
if (process.env.REACT_APP_INCLUDE_PROTECTED === "true") {
    WyKey = loadable(() => import("./components/WyKey/WyKey"));
}

// English lives at the site root; the other twelve languages carry a URL prefix.
const PREFIXED = SUPPORTED.filter((l) => l !== "en");

// Drives language selection on every navigation:
//   - A URL with a language prefix wins outright; we remember it in the cookie.
//   - On a non-prefixed (English-root) URL we resolve the visitor's language
//     from cookie > browser. If it isn't English we rewrite the URL to the
//     prefixed equivalent — this is both the first-visit auto-detect AND what
//     keeps the chosen language when an internal link drops the prefix
//     (e.g. href="/wykey" while browsing in French).
//   - Otherwise we settle on English and persist that choice.
// react-snap prerender follows the URL only (no cookie/navigator/redirect) so
// prerendered pages stay deterministic.
const LangManager: FC = () => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const urlLang = langFromUrl(pathname);

    useEffect(() => {
        const sync = (lang: string) => {
            if (i18n.language !== lang) i18n.changeLanguage(lang);
        };

        if (isPrerender()) {
            sync(urlLang || "en");
            return;
        }

        if (urlLang) {
            setLangCookie(urlLang);
            sync(urlLang);
            return;
        }

        const desired = resolveLang(pathname);
        if (desired !== "en") {
            setLangCookie(desired);
            const suffix = pathname === "/" ? "" : pathname;
            navigate(`/${desired}${suffix}`, { replace: true });
            return;
        }

        setLangCookie("en");
        sync("en");
    }, [pathname, urlLang, i18n, navigate]);

    return null;
};

// Routes are built from a plain object array so the language entries are never
// dropped by JSX children flattening: 3 pages at the English root + the same
// 3 under each of the twelve language prefixes.
const AppRoutes: FC = () =>
    useRoutes([
        { path: "/", element: <Pages /> },
        { path: "/demo", element: <Demo /> },
        { path: "/wykey", element: <WyKeyLight /> },
        { path: "/wykey/offer", element: <Offer /> },
        ...(INCLUDE_FULL ? [{ path: "/wykey/full", element: <WyKey /> }] : []),
        ...PREFIXED.flatMap((lng) => [
            { path: `/${lng}`, element: <Pages /> },
            { path: `/${lng}/demo`, element: <Demo /> },
            { path: `/${lng}/wykey`, element: <WyKeyLight /> },
            { path: `/${lng}/wykey/offer`, element: <Offer /> },
            ...(INCLUDE_FULL
                ? [{ path: `/${lng}/wykey/full`, element: <WyKey /> }]
                : []),
        ]),
        { path: "*", element: <Navigate to="/" replace /> },
    ]);

const App: FC = () => {
    return (
        <div className="App">
            <LangManager />
            <AppRoutes />
            <BackToTop />
        </div>
    );
};

export default App;
